export type StoryvineFieldKey = string;

export interface StoryvineField {
  key: StoryvineFieldKey;
  required: boolean;
  aliases: readonly string[];
}

export interface StoryvineFieldTarget {
  kind: 'storyvineField';
  fieldKey: StoryvineFieldKey;
}

export interface CustomFieldTarget {
  kind: 'customField';
  name: string;
}

export interface IgnoredTarget {
  kind: 'ignored';
}

export type MappingTarget = StoryvineFieldTarget | CustomFieldTarget | IgnoredTarget;

export interface CsvColumnMapping {
  csvHeader: string;
  columnIndex: number;
  target: MappingTarget | null;
  autoMapped: boolean;
}

export interface HydratedRosterMapping {
  id: string;
  name: string;
  columns: CsvColumnMapping[];
  identityColumn: string | null;
  connectionColumn: string | null;
}

export interface ParsedRoster {
  fileName: string;
  headers: string[];
  rows: string[][];
  totalRows: number;
  droppedEmptyHeaderColumns: number[];
}

export interface RowError {
  rowNumber: number;
  csvHeader: string;
  fieldKey: StoryvineFieldKey | null;
  reason: string;
}

export interface ResolvedRosterRow {
  fields: Record<StoryvineFieldKey, string>;
  customProperties: Record<string, string>;
  suspended: boolean | null;
}

export type RosterPathKind = 'mapped' | 'legacy';

export interface ResolvedRoster {
  rows: ResolvedRosterRow[];
  columns: CsvColumnMapping[];
  identityColumn: string | null;
  connectionColumn: string | null;
  pathKind: RosterPathKind;
  exampleValuesByHeader: Record<string, string[]>;
}

export type CustomFieldNameProblem = 'empty' | 'tooLong' | 'duplicate' | 'reserved';

export interface ParseErrorFailure {
  kind: 'parseError';
  message: string;
  rowNumber: number | null;
}

export interface EmptyFileFailure {
  kind: 'emptyFile';
}

export interface NoHeaderRowFailure {
  kind: 'noHeaderRow';
}

export interface NoDataRowsFailure {
  kind: 'noDataRows';
}

export interface XlsxNotSupportedInLegacyFailure {
  kind: 'xlsxNotSupportedInLegacy';
}

export interface DuplicateHeadersFailure {
  kind: 'duplicateHeaders';
  headers: string[];
}

export interface LegacyHeaderMismatchFailure {
  kind: 'legacyHeaderMismatch';
  expected: string[];
  received: string[];
}

export interface LegacyRowColumnCountFailure {
  kind: 'legacyRowColumnCount';
  rowNumber: number;
  expected: number;
  actual: number;
}

export interface RequiredFieldsUnmappedFailure {
  kind: 'requiredFieldsUnmapped';
  fieldKeys: StoryvineFieldKey[];
}

export interface UndecidedColumnsFailure {
  kind: 'undecidedColumns';
  csvHeaders: string[];
}

export interface CustomFieldNameInvalidFailure {
  kind: 'customFieldNameInvalid';
  csvHeader: string;
  name: string;
  problem: CustomFieldNameProblem;
}

export interface RowErrorsFailure {
  kind: 'rowErrors';
  errors: RowError[];
}

export type RosterUploadFailure =
  | ParseErrorFailure
  | EmptyFileFailure
  | NoHeaderRowFailure
  | NoDataRowsFailure
  | XlsxNotSupportedInLegacyFailure
  | DuplicateHeadersFailure
  | LegacyHeaderMismatchFailure
  | LegacyRowColumnCountFailure
  | RequiredFieldsUnmappedFailure
  | UndecidedColumnsFailure
  | CustomFieldNameInvalidFailure
  | RowErrorsFailure;

export const assertNever = (value: never): never => {
  throw new Error(`Unhandled variant: ${JSON.stringify(value)}`);
};
